import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import '../../App.css';
import { translateError } from '../../utils/errorMessages';

interface DashboardStats {
  totalOrganizations: number;
  totalUsers: number;
  totalMembers: number;
  activeSubscriptions: number;
  trialOrganizations: number;
  recentOrganizations: IOrganization[];
}

interface IOrganization {
  _id: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
  createdAt: string;
}

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.superAdmin.getDashboard();
      setStats(response.data);
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) return <p>{t('common.loading')}</p>;
  if (error) return <p className="error-message">{t('common.error')}: {error}</p>;

  return (
    <div className="admin-dashboard-page">
      <h2>{t('superAdmin.dashboard.title')}</h2>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats?.totalOrganizations || 0}</div>
          <div className="stat-label">{t('superAdmin.dashboard.totalOrganizations')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.totalUsers || 0}</div>
          <div className="stat-label">{t('superAdmin.dashboard.totalUsers')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.totalMembers || 0}</div>
          <div className="stat-label">{t('superAdmin.dashboard.totalMembers')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.activeSubscriptions || 0}</div>
          <div className="stat-label">{t('superAdmin.dashboard.activeSubscriptions')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.trialOrganizations || 0}</div>
          <div className="stat-label">{t('superAdmin.dashboard.trialOrganizations')}</div>
        </div>
      </div>

      <div className="dashboard-section">
        <div className="section-header">
          <h3>{t('superAdmin.dashboard.recentOrganizations')}</h3>
          <Link to="/super-admin/organizations" className="view-all-link">
            {t('superAdmin.dashboard.viewAll')}
          </Link>
        </div>

        {stats?.recentOrganizations && stats.recentOrganizations.length > 0 ? (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('common.name')}</th>
                  <th>{t('superAdmin.organizations.slug')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('superAdmin.organizations.createdAt')}</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentOrganizations.map((org) => (
                  <tr key={org._id}>
                    <td>
                      <Link to={`/super-admin/organizations/${org._id}`}>
                        {org.name}
                      </Link>
                    </td>
                    <td>{org.slug}</td>
                    <td>
                      <span className={`badge badge-status-${org.status}`}>
                        {t(`superAdmin.status.${org.status}`)}
                      </span>
                    </td>
                    <td>{new Date(org.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>{t('superAdmin.organizations.noOrganizations')}</p>
        )}
      </div>

      <div className="dashboard-section">
        <h3>{t('superAdmin.dashboard.quickActions')}</h3>
        <div className="quick-actions">
          <Link to="/super-admin/organizations/create" className="action-button">
            {t('superAdmin.organizations.createNew')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
